import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { usePublicTheme } from '../hooks/usePublicTheme';
import {
  SunIcon, MoonIcon, LockClosedIcon, EyeIcon, EyeSlashIcon, DocumentArrowDownIcon,
  CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, ShieldCheckIcon, BuildingOffice2Icon, UserIcon,
  EnvelopeIcon, PhoneIcon
} from '@heroicons/react/24/outline';

/* ---------- típusok (a Cloud Function publikus válasza) ---------- */
interface PubItem { productName: string; productCode: string; quantity: number; unitPrice: number; discount: number; taxRate: number; lineTotal: number; lineTotalWithTax: number }
interface PubParty { name: string; address?: { street?: string; city?: string; postalCode?: string; country?: string } | null; taxNumber?: string; email?: string; phone?: string; logoUrl?: string | null; contactName?: string }
interface PubQuotation {
  id: string; number: string; status: string; date: string | null; validUntil: string | null; paymentDue: string | null;
  currency: string; notes: string; items: PubItem[]; attachedPdf?: { url: string; name: string } | null; documents?: { name: string; url: string; size?: number | null }[]; subtotal: number; taxAmount: number; totalAmount: number; expired: boolean;
  customerResponse: { decision: 'accepted' | 'rejected'; name: string; comment: string; at: string | null } | null;
  organization: PubParty; customer: PubParty; viewCount: number;
}

const api = {
  get: httpsCallable<{ quotationId: string; shareId: string; password?: string }, { needsPassword?: boolean; number?: string; quotation?: PubQuotation }>(functions, 'publicQuotationGet'),
  respond: httpsCallable<{ quotationId: string; shareId: string; password?: string; decision: 'accepted' | 'rejected'; name?: string; comment?: string }, { quotation: PubQuotation }>(functions, 'publicQuotationRespond')
};

const money = (n: number, c = 'HUF') => new Intl.NumberFormat('hu-HU', { style: 'currency', currency: c, maximumFractionDigits: c === 'HUF' ? 0 : 2 }).format(n || 0);
const fmtDate = (iso: string | null) => {
  if (!iso) return '–';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '–' : `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.`;
};
const addr = (a?: PubParty['address']) => (a ? [[a.postalCode, a.city].filter(Boolean).join(' '), a.street].filter(Boolean).join(', ') : '');

const card = 'bg-white dark:bg-[#12151c] rounded-xl border border-gray-200 dark:border-[#2a2f3c]';
const input = 'w-full h-10 rounded-lg border border-gray-300 dark:border-[#2a2f3c] bg-white dark:bg-[#0b0d12] px-3 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40';
const btnPrimary = 'inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-[#0f7f75] text-white text-sm font-medium hover:bg-[#0c6b62] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-gray-300 dark:border-[#2a2f3c] text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50';

/** Oldalkeret – a komponensen KÍVÜL definiálva, különben minden billentyűleütésnél újra-mountolódna (elveszne a fókusz) */
const Shell: React.FC<{ children: React.ReactNode; wide?: boolean; xwide?: boolean; theme: string; toggleTheme: () => void }> = ({ children, wide, xwide, theme, toggleTheme }) => (
  <div className="min-h-screen bg-[#f7f7f9] dark:bg-[#0b0d12] text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
    <div className={`${xwide ? 'max-w-[1440px]' : wide ? 'max-w-4xl' : 'max-w-md'} mx-auto px-4 py-6 sm:py-10`}>
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          <ShieldCheckIcon className="w-3.5 h-3.5" /> Bizalmas dokumentum – kérjük, ne ossza meg illetéktelenekkel.
        </div>
        <button type="button" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Világos mód' : 'Sötét mód'}
          className="w-9 h-9 rounded-lg border border-gray-200 dark:border-[#2a2f3c] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center flex-shrink-0">
          {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
        </button>
      </div>
      {children}
      <p className="mt-8 text-center text-[11px] text-gray-400 dark:text-gray-500">Készült a YouCRM rendszerrel. Fejleszti a Webissimo Kft.</p>
    </div>
  </div>
);

const SharedQuotationView: React.FC = () => {
  const { quotationId } = useParams<{ quotationId: string }>();
  const [params] = useSearchParams();
  const shareId = params.get('share') || '';
  const { theme, toggle: toggleTheme } = usePublicTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState<PubQuotation | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [number, setNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);

  const [decision, setDecision] = useState<'accepted' | 'rejected' | null>(null);
  const [respName, setRespName] = useState('');
  const [respComment, setRespComment] = useState('');
  const [respBusy, setRespBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = async (pw?: string) => {
    if (!quotationId) { setError('Érvénytelen link'); setLoading(false); return; }
    try {
      const res = await api.get({ quotationId, shareId, password: pw });
      if (res.data.needsPassword) { setNeedsPassword(true); setNumber(res.data.number || ''); }
      else if (res.data.quotation) { setQ(res.data.quotation); setNeedsPassword(false); }
    } catch (e: any) {
      if (pw !== undefined) { setPwError(e?.message?.replace(/^.*?:\s*/, '') || 'Hibás jelszó'); }
      else setError(e?.message?.replace(/^.*?:\s*/, '') || 'Az árajánlat nem érhető el');
    } finally {
      setLoading(false);
      setPwBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [quotationId, shareId]);

  useEffect(() => { if (q) document.title = `Árajánlat ${q.number} – ${q.organization?.name || ''}`; }, [q]);

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null); setPwBusy(true);
    load(password);
  };

  const respond = async () => {
    if (!q || !decision || !quotationId) return;
    if (!respName.trim()) return;
    try {
      setRespBusy(true);
      const res = await api.respond({ quotationId, shareId, password: password || undefined, decision, name: respName.trim(), comment: respComment.trim() });
      setQ(res.data.quotation);
      setDecision(null);
    } catch (e: any) {
      alert(e?.message?.replace(/^.*?:\s*/, '') || 'A válasz rögzítése nem sikerült');
    } finally {
      setRespBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!q) return;
    // Csatolt (máshol elkészített) PDF: magát a csatolt fájlt töltjük le, nem generálunk
    if (q.attachedPdf?.url) {
      const name = q.attachedPdf.name || `${q.number || 'arajanlat'}.pdf`;
      try {
        setPdfBusy(true);
        const res = await fetch(q.attachedPdf.url);
        if (!res.ok) throw new Error(String(res.status));
        const blobUrl = URL.createObjectURL(await res.blob());
        const a = document.createElement('a');
        a.href = blobUrl; a.download = name; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      } catch {
        // ha a tárhely nem engedi a közvetlen letöltést (CORS), új lapon nyitjuk meg
        window.open(q.attachedPdf.url, '_blank', 'noopener');
      } finally {
        setPdfBusy(false);
      }
      return;
    }
    try {
      setPdfBusy(true);
      const { generateHungarianPdf } = await import('../components/quotations/generateHungarianPdf');
      const quotationLike: any = {
        id: q.id, number: q.number, quotationNumber: q.number, status: q.status, currency: q.currency,
        date: q.date, validUntil: q.validUntil, paymentDue: q.paymentDue, createdAt: q.date, notes: q.notes,
        items: q.items.map(i => ({ ...i, price: i.unitPrice, currency: q.currency })),
        subtotal: q.subtotal, taxAmount: q.taxAmount, totalAmount: q.totalAmount, contactName: q.customer.contactName
      };
      const orgLike: any = { name: q.organization.name, address: q.organization.address, taxNumber: q.organization.taxNumber, logoUrl: q.organization.logoUrl, contact: { email: q.organization.email, phone: q.organization.phone } };
      const custLike: any = { type: 'organization', companyName: q.customer.name, address: q.customer.address, taxNumber: q.customer.taxNumber, email: q.customer.email };
      const doc = await generateHungarianPdf(null, quotationLike, orgLike, custLike, []);
      doc.save(`${q.number || 'arajanlat'}.pdf`);
    } catch (e) {
      console.error(e);
      alert('A PDF létrehozása nem sikerült');
    } finally {
      setPdfBusy(false);
    }
  };


  if (loading) {
    return (
      <Shell theme={theme} toggleTheme={toggleTheme} wide>
        <div className={`${card} p-8 animate-pulse space-y-4`}>
          <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-32 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell theme={theme} toggleTheme={toggleTheme}>
        <div className={`${card} p-8 text-center`}>
          <XCircleIcon className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold">Az árajánlat nem érhető el</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{error}</p>
        </div>
      </Shell>
    );
  }

  if (needsPassword && !q) {
    return (
      <Shell theme={theme} toggleTheme={toggleTheme}>
        <form onSubmit={submitPassword} className={`${card} p-6 sm:p-8`}>
          <div className="w-11 h-11 rounded-xl bg-[#0f7f75]/10 text-[#0f7f75] flex items-center justify-center mb-4"><LockClosedIcon className="w-5 h-5" /></div>
          <h1 className="text-lg font-semibold">Jelszóval védett árajánlat</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{number ? `A(z) ${number} ` : 'Az '}árajánlat megnyitásához adja meg a kapott jelszót.</p>
          <div className="relative mt-5">
            <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className={input + ' pr-10'} placeholder="Jelszó" autoFocus />
            <button type="button" onClick={() => setShowPw(s => !s)} className="absolute inset-y-0 right-0 px-3 text-gray-400">{showPw ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}</button>
          </div>
          {pwError && <p className="mt-2 text-sm text-red-500">{pwError}</p>}
          <button type="submit" disabled={pwBusy || !password} className={btnPrimary + ' w-full mt-4'}>{pwBusy ? 'Ellenőrzés…' : 'Megnyitás'}</button>
        </form>
      </Shell>
    );
  }

  if (!q) return null;

  const responded = q.customerResponse;
  const accepted = q.status === 'accepted' || q.status === 'success' || responded?.decision === 'accepted';
  const rejected = q.status === 'rejected' && !accepted;
  const canRespond = !accepted && !q.expired;
  const taxByRate = q.items.reduce<Record<string, number>>((acc, i) => { acc[i.taxRate] = (acc[i.taxRate] || 0) + (i.lineTotalWithTax - i.lineTotal); return acc; }, {});

  // Csatolt PDF-es ajánlat: bal oldalt a részletek, jobb oldalt a PDF-olvasó
  const isPdf = !!q.attachedPdf?.url;

  return (
    <Shell theme={theme} toggleTheme={toggleTheme} wide xwide={isPdf}>
      {/* Állapot sáv */}
      {accepted && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">
          <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
          <div>Ezt az árajánlatot elfogadták{responded?.name ? ` (${responded.name}, ${fmtDate(responded.at)})` : ''}. Köszönjük! Hamarosan felvesszük Önnel a kapcsolatot a következő lépésekkel.</div>
        </div>
      )}
      {rejected && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-900/20 px-4 py-3 text-sm text-rose-800 dark:text-rose-300">
          <XCircleIcon className="w-5 h-5 flex-shrink-0" />
          <div>Ezt az árajánlatot elutasították{responded?.name ? ` (${responded.name}, ${fmtDate(responded.at)})` : ''}. Ha meggondolta magát, az alábbi gombbal elfogadhatja.</div>
        </div>
      )}
      {!accepted && q.expired && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
          <div>Az árajánlat érvényessége {fmtDate(q.validUntil)}-án lejárt. Kérjük, vegye fel a kapcsolatot velünk új ajánlatért.</div>
        </div>
      )}

      <div className={isPdf ? 'lg:grid lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] lg:gap-6 lg:items-start' : ''}>
      <div className="min-w-0">
      {/* Dokumentum */}
      <div className={`${card} overflow-hidden`}>
        <div className="h-1 bg-[#0f7f75]" />
        <div className={isPdf ? 'p-5 sm:p-6' : 'p-5 sm:p-8'}>
          {/* fejléc */}
          <div className={isPdf ? 'flex flex-col gap-3' : 'flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4'}>
            <div className="min-w-0">
              {q.organization.logoUrl ? (
                <img src={q.organization.logoUrl} alt={q.organization.name} className="h-10 max-w-[180px] object-contain mb-2" />
              ) : (
                <div className="text-lg font-semibold">{q.organization.name}</div>
              )}
            </div>
            <div className={isPdf ? '' : 'sm:text-right'}>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">Árajánlat</div>
              <div className="text-2xl font-semibold text-[#0f7f75] tracking-tight">{q.number}</div>
            </div>
          </div>

          <div className={`mt-6 grid grid-cols-2 ${isPdf ? '' : 'sm:grid-cols-4'} gap-4 border-y border-gray-200 dark:border-[#2a2f3c] py-4`}>
            {[['Kelt', fmtDate(q.date)], ['Érvényes', fmtDate(q.validUntil)], ['Fizetési határidő', q.paymentDue ? `${q.paymentDue} nap` : '–'], ['Pénznem', q.currency]].map(([k, v]) => (
              <div key={k}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">{k}</div>
                <div className="text-sm font-medium mt-0.5">{v}</div>
              </div>
            ))}
          </div>

          {/* felek */}
          <div className={`mt-6 grid grid-cols-1 ${isPdf ? '' : 'sm:grid-cols-2'} gap-6`}>
            {([['Kiállító', q.organization, BuildingOffice2Icon], ['Ügyfél', q.customer, UserIcon]] as [string, PubParty, React.ElementType][]).map(([title, p, Icon]) => (
              <div key={title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-gray-500 dark:text-gray-400" /></div>
                <div className="min-w-0 text-sm">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">{title}</div>
                  <div className="font-medium mt-0.5">{p.name || '–'}</div>
                  {addr(p.address) && <div className="text-gray-600 dark:text-gray-300">{addr(p.address)}</div>}
                  {p.taxNumber && <div className="text-xs text-gray-500 dark:text-gray-400">Adószám: {p.taxNumber}</div>}
                  <div className="flex flex-wrap gap-x-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:underline"><EnvelopeIcon className="w-3.5 h-3.5" />{p.email}</a>}
                    {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:underline"><PhoneIcon className="w-3.5 h-3.5" />{p.phone}</a>}
                  </div>
                  {p.contactName && <div className="text-xs text-gray-500 dark:text-gray-400">Kapcsolattartó: {p.contactName}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* tételek */}
          <div className="mt-8">
            {isPdf ? null : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60">
                    <th className="text-left font-semibold px-3 py-2 rounded-l-lg w-8">#</th>
                    <th className="text-left font-semibold px-3 py-2">Megnevezés</th>
                    <th className="text-right font-semibold px-3 py-2">Menny.</th>
                    <th className="text-right font-semibold px-3 py-2">Egységár</th>
                    <th className="text-right font-semibold px-3 py-2">Kedv.</th>
                    <th className="text-right font-semibold px-3 py-2">ÁFA</th>
                    <th className="text-right font-semibold px-3 py-2">Nettó</th>
                    <th className="text-right font-semibold px-3 py-2 rounded-r-lg">Bruttó</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-[#2a2f3c]">
                  {q.items.map((i, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-3 text-gray-400 tabular-nums">{idx + 1}</td>
                      <td className="px-3 py-3"><div className="font-medium">{i.productName}</div>{i.productCode && <div className="text-xs text-gray-500 dark:text-gray-400">{i.productCode}</div>}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{i.quantity}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{money(i.unitPrice, q.currency)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-500">{i.discount ? `${i.discount}%` : '–'}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-500">{i.taxRate}%</td>
                      <td className="px-3 py-3 text-right tabular-nums">{money(i.lineTotal, q.currency)}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium">{money(i.lineTotalWithTax, q.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-100 dark:divide-[#2a2f3c]">
              {q.items.map((i, idx) => (
                <li key={idx} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{i.productName}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{i.quantity} × {money(i.unitPrice, q.currency)}{i.discount ? ` · −${i.discount}%` : ''} · ÁFA {i.taxRate}%</div>
                  </div>
                  <div className="text-sm font-medium tabular-nums whitespace-nowrap">{money(i.lineTotalWithTax, q.currency)}</div>
                </li>
              ))}
            </ul>
            </>
            )}

            <div className={isPdf ? '' : 'mt-4 flex justify-end'}>
              <div className={`w-full ${isPdf ? '' : 'sm:w-80'} rounded-xl bg-[#0f7f75]/5 dark:bg-[#0f7f75]/10 p-4 text-sm space-y-1.5`}>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Nettó összesen</span><span className="tabular-nums">{money(q.subtotal, q.currency)}</span></div>
                {q.attachedPdf?.url && q.totalAmount > q.subtotal && (
                  <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">ÁFA</span><span className="tabular-nums">{money(q.totalAmount - q.subtotal, q.currency)}</span></div>
                )}
                {Object.entries(taxByRate).sort((a, b) => Number(b[0]) - Number(a[0])).map(([r, v]) => (
                  <div key={r} className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">ÁFA {r}%</span><span className="tabular-nums">{money(v, q.currency)}</span></div>
                ))}
                <div className="flex justify-between items-baseline pt-2 mt-1 border-t border-[#0f7f75]/30"><span className="font-semibold">Bruttó végösszeg</span><span className="text-xl font-semibold text-[#0f7f75] tabular-nums">{money(q.totalAmount, q.currency)}</span></div>
              </div>
            </div>
          </div>

          {!!q.documents?.length && (
            <div className="mt-8">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Mellékletek</div>
              <ul className="divide-y divide-gray-100 dark:divide-[#2a2f3c] rounded-xl border border-gray-200 dark:border-[#2a2f3c]">
                {q.documents.map((d, i) => (
                  <li key={i} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                    <DocumentArrowDownIcon className="w-4 h-4 text-gray-400 shrink-0" />
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-[#0f7f75] hover:underline">{d.name}</a>
                    {d.size ? <span className="text-xs text-gray-500 tabular-nums">{d.size > 1048576 ? `${(d.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(d.size / 1024))} KB`}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {q.notes && (
            <div className="mt-8">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Megjegyzés</div>
              <p className="text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-200">{q.notes}</p>
            </div>
          )}

          <div className="mt-8 text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <div className="text-[11px] uppercase tracking-wide font-semibold mb-1.5">Feltételek</div>
            <p>• Az árajánlat a fent megjelölt időpontig érvényes.</p>
            <p>• Az árak a feltüntetett ÁFA-tartalommal értendők; elfogadás után az árak rögzítettek.</p>
            <p>• {q.paymentDue ? `Fizetési feltétel: ${q.paymentDue} nap a számla kiállításától.` : 'Fizetési feltétel: a számlán feltüntetett határidő szerint.'}</p>
            <p>• Az árajánlat aláírás és pecsét nélkül is érvényes.</p>
          </div>
        </div>
      </div>

      {/* Műveletek */}
      <div className={`${card} mt-4 p-4 sm:p-5`}>
        {decision ? (
          <div className="space-y-3">
            <div className="text-sm font-semibold">{decision === 'accepted' ? 'Árajánlat elfogadása' : 'Árajánlat elutasítása'}</div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Az Ön neve *</label>
              <input value={respName} onChange={e => setRespName(e.target.value)} className={input} placeholder="Teljes név" autoFocus />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Megjegyzés (opcionális)</label>
              <textarea value={respComment} onChange={e => setRespComment(e.target.value)} rows={3} className={input.replace('h-10', 'py-2')} placeholder={decision === 'accepted' ? 'Pl. kért kezdési időpont, számlázási megjegyzés…' : 'Pl. miért nem megfelelő az ajánlat…'} />
            </div>
            {decision === 'accepted' && <p className="text-xs text-gray-500 dark:text-gray-400">Az elfogadással jelzi, hogy a fenti tartalommal és összeggel megrendeli a szolgáltatást / terméket. A részletekről kollégánk felveszi Önnel a kapcsolatot.</p>}
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={() => setDecision(null)} className={btnSecondary} disabled={respBusy}>Mégse</button>
              <button type="button" onClick={respond} disabled={respBusy || !respName.trim()} className={decision === 'accepted' ? btnPrimary : btnSecondary + ' !border-rose-300 !text-rose-600 dark:!text-rose-400'}>
                {respBusy ? 'Küldés…' : decision === 'accepted' ? 'Elfogadom az ajánlatot' : 'Elutasítom az ajánlatot'}
              </button>
            </div>
          </div>
        ) : (
          <div className={isPdf ? 'flex flex-col gap-2' : 'flex flex-col sm:flex-row sm:items-center gap-2'}>
            <button type="button" onClick={downloadPdf} disabled={pdfBusy} className={btnSecondary + (isPdf ? '' : ' sm:mr-auto')}><DocumentArrowDownIcon className="w-4 h-4" />{pdfBusy ? (isPdf ? 'Letöltés…' : 'PDF készül…') : (isPdf ? 'Ajánlat letöltése (PDF)' : 'Letöltés PDF-ben')}</button>
            {canRespond && (
              <>
                {!rejected && <button type="button" onClick={() => setDecision('rejected')} className={btnSecondary}><XCircleIcon className="w-4 h-4" />Elutasítom</button>}
                <button type="button" onClick={() => setDecision('accepted')} className={btnPrimary}><CheckCircleIcon className="w-4 h-4" />Elfogadom az ajánlatot</button>
              </>
            )}
          </div>
        )}
      </div>
      </div>

      {isPdf && (
        <aside className={`${card} overflow-hidden mt-4 lg:mt-0 lg:sticky lg:top-6`}>
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-[#2a2f3c] text-sm">
            <DocumentArrowDownIcon className="w-4 h-4 text-gray-400 shrink-0" />
            <span className="min-w-0 flex-1 truncate font-medium">{q.attachedPdf!.name}</span>
            <a href={q.attachedPdf!.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0f7f75] font-medium hover:underline whitespace-nowrap">Megnyitás új lapon</a>
          </div>
          <iframe title="Árajánlat" src={`${q.attachedPdf!.url}#view=FitH`} className="block w-full h-[75vh] lg:h-[calc(100vh-7rem)] bg-gray-50 dark:bg-gray-900" />
        </aside>
      )}
      </div>
    </Shell>
  );
};

export default SharedQuotationView;
